import React from "react";
import clsx from "clsx";
import _ from "lodash";

import PieChart from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import ReactDOMServer from "react-dom/server";

import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";
import {Chip} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";

import StepName from "../../RiskProfiling/components/StepContent/components/step/DynamicStep/components/StepName";
import ExtraTexts from "../../RiskProfiling/components/StepContent/components/step/DynamicStep/components/ExtraTexts";
import PageLoadingIndicator from "../../RiskProfiling/components/PageLoadingIndicator";
import {withEuroOrDash} from "../../../utils/utils";
import {toGermanFormat} from "../../../utils/numberFormater";
import AggregatedDepotsSwitch
  from "../../CustomerDashboardV2/components_v2/AggregatedDepotsSwitch/AggregatedDepotsSwitch";
import ChartLegend from "../../../components/Charts/Legend";
import {buildAssets, buildLiabilities, getFilteredCategories} from "../utils";
import {INFO_COLOR} from "../../../themes/themeConfigurator";
import {
  DEPOT_ASSET_TYPE,
  ASSETS_LIABILITIES_ACTIONS,
  ASSETS_LIABILITIES_TYPES,
  ASSETS_LIABILITIES_CONFIG
} from "../constants";
import CreateAssetModal from "../CreateAssetModal";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import useExpandedWithRegistry from "../../CustomerDashboardV2/hooks/useExpandedWithRegistry";
import InfoIcon from "../InfoIcon";

import styles from "./styles";

const ChartLegendTitle = ({classes, item}) => (
  <Grid item xs className={classes.chartLegendContainer}>
    <ChartLegend
      legendItems={
        [{
          name: item.title,
          color: item.color,
        }]
      }
      classes={classes}
    />
  </Grid>
);

const AssetsSection = ({classes, data, expanded, setExpanded, customerId, onAdd}) => {
  const noData = data.items.length === 0;
  const total = data.total_value;

  const [addAssetModalOpen, setAddAssetModalOpen] = React.useState(false);

  const handleAssetsUpdate = async (currentCategoryId, formData, close=false) => {
    if(close) closeAddAssetModal();
    onAdd(ASSETS_LIABILITIES_TYPES.ASSETS, ASSETS_LIABILITIES_ACTIONS.CREATE, currentCategoryId, formData)
  };

  const handleAddClick = () => setAddAssetModalOpen(true);
  const closeAddAssetModal = () => setAddAssetModalOpen(false);
  const handleAggregateChange = (value) => setExpanded(!value);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems={"center"}>
          <Grid item xs className={classes.sectionTitle}>
            Sonst. Vermögenswerte
          </Grid>
          <Grid item xs={'auto'}>
            <AggregatedDepotsSwitch
              value={!noData && !expanded}
              onChange={handleAggregateChange}
              disabled={noData}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Chip
          className={clsx(classes.totalValueChip, total > 0 && 'green')}
          label={toGermanFormat(total, '', ' €')}
        />
      </Grid>
      {expanded && data.items.map((category) => (
        <Grid item xs={12} key={category.id}>
          <Grid container spacing={1} alignItems={'center'}>
            <ChartLegendTitle classes={classes} item={category} />
            <Grid item xs={'auto'}><b>{withEuroOrDash(category.total_value)}</b></Grid>
          </Grid>
        </Grid>
      ))}
      {noData && (
        <Grid item xs={12}>Keine Vermögenswerte vorhanden</Grid>
      )}
      {!!onAdd && (
        <Grid item xs={12}>
          <PrimaryButton
            text={'Vermögenswert hinzufügen'}
            icon={<i className={'fal fa-plus'} />}
            onButtonClick={handleAddClick}
            disabled={data.loading}
            variant={'text'}
          />
          <CreateAssetModal
            open={addAssetModalOpen}
            onModalClose={closeAddAssetModal}
            onAssetCreate={handleAssetsUpdate}
            customerId={customerId}
            objectType={ASSETS_LIABILITIES_TYPES.ASSETS}
            itemsForLinks={data.itemsForLinks}
          />
        </Grid>
      )}
    </Grid>
  )
};

const DepotsSection = ({classes, loading, data, expanded, setExpanded}) => {
  const assets = _.get(data, ['assets'], []);
  const noData = assets.length === 0;
  const total = _.get(data, 'total_value', 0);
  const handleAggregateChange = (value) => setExpanded(!value);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems={"center"}>
          <Grid item xs className={classes.sectionTitle}>
            Depots
          </Grid>
          <Grid item xs={'auto'}>
            <AggregatedDepotsSwitch
              value={!noData && !expanded}
              onChange={handleAggregateChange}
              disabled={noData}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Chip
          className={clsx(classes.totalValueChip, (loading || total > 0) && 'blue')}
          label={loading
            ? (
              <Grid container className={classes.chipLoading} alignItems={"center"} spacing={2}>
                <Grid item>
                  <CircularProgress className={classes.loadingIndicator} style={{height: 30,width: 30, display: 'block'}} />
                </Grid>
                <Grid item>Daten werden geladen ...</Grid>
              </Grid>
            )
            : toGermanFormat(total, '', ' €')}
        />
      </Grid>
      {expanded && assets.map((ptf) => (
        <Grid item xs={12} key={ptf.id}>
          <Grid container spacing={1} alignItems={'center'} wrap={"nowrap"}>
            <ChartLegendTitle classes={classes} item={ptf} />
            <Grid item style={{marginRight: "auto", whiteSpace: 'nowrap'}}>
              <b>{withEuroOrDash(ptf.asset_value)}</b>
            </Grid>
          </Grid>
        </Grid>
      ))}
      {!loading && noData && (
        <Grid item xs={12}>Keine Depots vorhanden</Grid>
      )}
    </Grid>
  )
};

const LiabilitiesSection = ({classes, data, expanded, setExpanded, customerId, onAdd}) => {
  const noData = data.items.length === 0;
  const total = data.total_value;
  const objectType = ASSETS_LIABILITIES_TYPES.LIABILITIES;

  const [addAssetModalOpen, setAddAssetModalOpen] = React.useState(false);

  const handleAssetsUpdate = async (currentCategoryId, formData, close=false) => {
    if(close) closeAddAssetModal();
    onAdd(objectType, ASSETS_LIABILITIES_ACTIONS.CREATE, currentCategoryId, formData)
  };

  const handleAddClick = () => setAddAssetModalOpen(true);
  const closeAddAssetModal = () => setAddAssetModalOpen(false);
  const handleAggregateChange = (value) => setExpanded(!value);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems={"center"}>
          <Grid item xs className={classes.sectionTitle}>
            {ASSETS_LIABILITIES_CONFIG[objectType].pluralLabel}
          </Grid>
          <Grid item xs={'auto'}>
            <AggregatedDepotsSwitch
              value={!noData && !expanded}
              onChange={handleAggregateChange}
              disabled={noData}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Chip
          className={clsx(classes.totalValueChip, total > 0 && 'orange')}
          label={toGermanFormat(total, '', ' €')}
        />
      </Grid>
      {expanded && data.items.map((category) => (
        <Grid item xs={12} key={category.id}>
          <Grid container spacing={1} alignItems={'center'}>
            <ChartLegendTitle classes={classes} item={category} />
            <Grid item xs={'auto'}><b>{withEuroOrDash(category.total_value)}</b></Grid>
          </Grid>
        </Grid>
      ))}
      {noData && (
        <Grid item xs={12}>Keine {ASSETS_LIABILITIES_CONFIG[objectType].pluralLabel} vorhanden</Grid>
      )}
      {!!onAdd && (
        <Grid item xs={12}>
          <PrimaryButton
            text={ASSETS_LIABILITIES_CONFIG[objectType].addLabel}
            icon={<i className={'fal fa-plus'} />}
            onButtonClick={handleAddClick}
            disabled={data.loading}
            variant={'text'}
          />
          <CreateAssetModal
            open={addAssetModalOpen}
            onModalClose={closeAddAssetModal}
            onAssetCreate={handleAssetsUpdate}
            customerId={customerId}
            objectType={objectType}
            itemsForLinks={data.itemsForLinks}
          />
        </Grid>
      )}
    </Grid>
  )
};

const dataLabelsOptions = {
  connectorWidth: 0,
  formatter: function() {
    if (this.point.icon) {
      return ReactDOMServer.renderToString(<span style={{fontSize: 16, color: '#fff'}}>{this.point.icon}</span>); //icon
    }
  },
  padding: 0,
  color: 'white',
  crop: false,
  distance: -20,
  useHTML: true,
  overflow: 'allow'
};

const prepareSeriesData = (items, total, outerTotal) => (items || []).map(category => {
  const value = category.total_value || category.asset_value;  // for depots
  const weight = value / total;
  const overallWeight = outerTotal === undefined ? weight : (value / outerTotal);
  return {
    name: category.title,
    color: category.color,
    y: weight < 0.01 ? 0.01 : weight, // use 1 % for category if it is less then 1%
    icon: (overallWeight >= 0.05) ? category.icon : undefined, // don't show icon if category is less then 5% as it won't fit
    amount: withEuroOrDash(value),
  }
});

const TotalChartValue = ({classes, totalValue}) => {
  const valueAsStr = withEuroOrDash(totalValue);
  const maxChars = 12;

  return (
    <b className={clsx(classes.totalChartValue, valueAsStr.length === maxChars && 'mid', valueAsStr.length > maxChars && 'long')}>
      {valueAsStr}
    </b>
  )
};

const TotalChart = React.memo(({classes, loading, assetsData, liabilitiesData, depotsData, expandedAssets, expandedLiabilities, expandedDepots}) => {
  const assetsTotalValue = (assetsData.total_value || 0) + (depotsData.total_value || 0);
  const assets = (!expandedAssets && (assetsData.items || []).length)
    ? {...assetsData, items: [{title: 'Sonst. Vermögenswerte', color: '#00A643', total_value: assetsData.total_value}]}
    : assetsData;
  const liabilities = (!expandedLiabilities && (liabilitiesData.items || []).length)
    ? {...liabilitiesData, items: [{title: 'Verbindlichkeiten', color: '#F57C00', total_value: liabilitiesData.total_value}]}
    : liabilitiesData;
  const depots = (!expandedDepots && (depotsData.assets || []).length)
    ? {...depotsData, items: [{title: DEPOT_ASSET_TYPE.title, color: INFO_COLOR, total_value: depotsData.total_value}]}
    : {...depotsData, items: (depotsData.assets || []).map(a => ({...a, icon: DEPOT_ASSET_TYPE.reactIcon}))};
  const assetsAndDepots = {total_value: assetsTotalValue, items: [...(assets.items || []), ...(depots.items || [])]};

  const [outerData, innerData] = assetsTotalValue >= liabilitiesData.total_value
    ? [assetsAndDepots, liabilities]
    : [liabilities, assetsAndDepots];

  const totalValue = assetsTotalValue - liabilities.total_value;

  const isEmpty = loading || _.isEmpty(outerData.items);

  return (
    <PieChart
      highcharts={Highcharts}
      options={{
        chart: {
          type: 'pie',
          height: 375,
          margin: 0,
          spacing: [0, 0, 0, 0],
          backgroundColor: 'transparent',
        },
        credits: {
          enabled: false,
        },
        title: {
          style: {
            textAlign: 'center',
          },
          text:
            ReactDOMServer.renderToString(
              loading ?
                <>
                  <div style={{textAlign: 'center'}}>
                    <CircularProgress className={classes.loadingIndicator} style={{height: 30, width: 30}} />
                  </div>
                  <div>
                    <b style={{fontSize: 16, fontFamily: 'Roboto-Bold'}}>Daten werden geladen ...</b>
                  </div>
                </> :
                <>
                  <span className={classes.totalChartLabel}>Gesamtvermögen</span>
                  <br/><TotalChartValue classes={classes} totalValue={totalValue} />
                </>
            ),
          verticalAlign: 'middle',
          useHTML: true,
          y: -10,
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          enabled: !isEmpty,
          useHTML: true,
          outside: true,
          shadow: false,
          padding: 0,
          backgroundColor: 'transparent',
          borderWidth: 0,
          formatter: function () {
            return `<div style="border-left: 5px solid ${this.point.color}; padding: 16px; font-size: 15px; white-space: nowrap; display: inline-flex; background: rgba(255,255,255,0.95); box-shadow: 0 2px 10px rgba(0,0,0,.15);">
                      <div style="max-width: 250px; padding-right: 1em; overflow: hidden; text-overflow: ellipsis;">${this.point.name}</div>
                      <b style="margin-left: auto">${this.point.amount}</b>
                    </div>`
          },
        },
        plotOptions: {
          pie: {
            size: "100%",
            states: {
              hover: {
                enabled: !isEmpty,
                halo: {
                  opacity: 0.9,
                  size: 5,
                }
              }
            }
          },
        },
        series: [{
          type: "pie",
          size: "100%",
          innerSize: '78%',
          data: isEmpty
            ? [{color: '#D9DCDF', y: 1, amount: totalValue}]
            : prepareSeriesData(outerData.items, outerData.total_value),
          dataLabels: dataLabelsOptions
        }, {
          type: "pie",
          size: '73%',
          innerSize: '68%',
          endAngle: 360 * (innerData.total_value / outerData.total_value),
          data: prepareSeriesData(innerData.items, innerData.total_value, outerData.total_value),
          dataLabels: dataLabelsOptions
        }]
      }}
    />
  )
});

const Summary = (props) => {
  const {
    classes,
    assetsAndLiabilitiesData,
    depotsAndCashData,
    stepName,
    extra_texts,
    customerId,
    onAdd,
    withOtherAssets,
    withLiabilities,
    showNewBadge,
  } = props;

  const [expandedAssets, setExpandedAssets] = useExpandedWithRegistry('overviewTabSections.otherAssetsSummary.assets');
  const [expandedDepots, setExpandedDepots] = useExpandedWithRegistry('overviewTabSections.otherAssetsSummary.depots');
  const [expandedLiabilities, setExpandedLiabilities] = useExpandedWithRegistry('overviewTabSections.otherAssetsSummary.liabilities');

  const [assets, liabilities, depots] = React.useMemo(() => {

    const assetsData = buildAssets(assetsAndLiabilitiesData, depotsAndCashData, false);
    assetsData.items = getFilteredCategories(assetsData.items, false);

    const liabilitiesData = buildLiabilities(assetsAndLiabilitiesData);
    liabilitiesData.items = getFilteredCategories(liabilitiesData.items, false);

    return [
      assetsData,
      liabilitiesData,
      buildAssets(assetsAndLiabilitiesData, depotsAndCashData, true, false, false).items[0] || {},
    ]
  }, [assetsAndLiabilitiesData.updatedAt, depotsAndCashData.updatedAt]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems={'center'}>
          <Grid item xs={12} className={classes.stepName}>
            <StepName name={stepName} /><InfoIcon showNewBadge={showNewBadge} />
          </Grid>
          {!!extra_texts && (
            <Grid item xs={12}>
              <ExtraTexts extra_texts={extra_texts} />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {assetsAndLiabilitiesData.loading ? <PageLoadingIndicator /> : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
              <Grid container spacing={3}>
                {withOtherAssets && (
                  <Grid item xs={12}>
                    <AssetsSection
                      classes={classes}
                      data={assets}
                      expanded={expandedAssets}
                      setExpanded={setExpandedAssets}
                      customerId={customerId}
                      onAdd={onAdd}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <DepotsSection
                    classes={classes}
                    data={depots}
                    loading={depotsAndCashData.loading}
                    expanded={expandedDepots}
                    setExpanded={setExpandedDepots}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} lg={4} className={classes.totalChartSection}>
              <TotalChart
                classes={classes}
                loading={depotsAndCashData.loading}
                assetsData={assets}
                liabilitiesData={liabilities}
                depotsData={depots}
                expandedAssets={expandedAssets}
                expandedLiabilities={expandedLiabilities}
                expandedDepots={expandedDepots}
              />
            </Grid>
            {withLiabilities && (
              <Grid item xs={12} md={6} lg={4}>
                <LiabilitiesSection
                  classes={classes}
                  data={liabilities}
                  expanded={expandedLiabilities}
                  setExpanded={setExpandedLiabilities}
                  customerId={customerId}
                  onAdd={onAdd}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  )
};

Summary.defaultProps = {
  withOtherAssets: true,
  withLiabilities: true
};

export default withStyles(styles)(Summary);